import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationService, AppNotification } from '../services/notificationService';

const POLL_INTERVAL_MS = 30_000;

export function useNotifications(userId: string | null) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetch = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await notificationService.getNotifications(userId);
      setNotifications(data);
    } catch {
      // silently swallow polling errors
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetch();
    intervalRef.current = setInterval(fetch, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetch]);

  const markRead = useCallback(async (notificationId: string) => {
    if (!userId) return;
    await notificationService.markRead(userId, notificationId);
    setNotifications(prev =>
      prev.map(n => n.notificationId === notificationId ? { ...n, read: true } : n)
    );
  }, [userId]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    await notificationService.markAllRead(userId);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [userId]);

  const dismiss = useCallback(async (notificationId: string) => {
    if (!userId) return;
    await notificationService.dismiss(userId, notificationId);
    setNotifications(prev => prev.filter(n => n.notificationId !== notificationId));
  }, [userId]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return { notifications, unreadCount, loading, markRead, markAllRead, dismiss, refresh: fetch };
}
